import { STORAGE_KEYS } from '../config/constants.js';
import type { AuthToken } from '../models/user.js';

export type AuthTokenProvider = () => AuthToken | null;

let authTokenProvider: AuthTokenProvider | null = null;

/**
 * Register a live token source. While one is set it is authoritative for
 * `getAuthToken()` — the stored token is not consulted, even when the provider
 * returns null — so a session renewed elsewhere (e.g. the OIDC runtime's silent
 * refresh) is picked up by the very next request. Pass null to unregister.
 */
export function setAuthTokenProvider(provider: AuthTokenProvider | null): void {
  authTokenProvider = provider;
}

export function setAuthToken(token: AuthToken, remember = true): void {
  try {
    const target = remember ? localStorage : sessionStorage
    target.setItem(STORAGE_KEYS.AUTH_TOKEN, JSON.stringify(token));
  } catch (error) {
    console.error('Failed to save auth token:', error);
  }
}

export function getAuthToken(): AuthToken | null {
  if (authTokenProvider) return authTokenProvider();
  try {
    const sessionTokenJson = sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const localTokenJson = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const tokenJson = sessionTokenJson ?? localTokenJson;
    if (!tokenJson) return null;

    const token: AuthToken = JSON.parse(tokenJson) as AuthToken;

    return token;
  } catch (error) {
    console.error('Failed to retrieve auth token:', error);
    return null;
  }
}

export function removeAuthToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('Failed to remove auth token:', error);
  }
}

export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
} 